// The live conversation of a run: which conversation it is (from a pending question, or the
// device's conversation list), subscribed and decrypted with this browser's device key.
import { useQuery } from "@tanstack/react-query";

import type { Question } from "../../api/client";
import { conversationsQuery } from "../../api/queries";
import { useConversation } from "../../conversation/useConversation";
import { type DeviceState, useDevice } from "../../device/context";
import { useT } from "../../i18n";
import { Loading } from "../../ui/states";
import { ConversationCard } from "./ConversationCard";

export interface ConversationSlotProps {
  runId: string;
  questions: Question[];
  agent: string;
  where?: string;
}

export function RunConversation(props: ConversationSlotProps) {
  const t = useT();
  const device = useDevice();
  if (device.kind === "loading") return <Loading label={t.conversation.checkingKeys} />;
  if (device.kind === "unlinked") return <ConversationCard source={{ kind: "unlinked" }} {...props} />;
  return <LinkedRunConversation device={device} {...props} />;
}

function LinkedRunConversation({ device, ...props }: ConversationSlotProps & { device: DeviceState & { kind: "linked" } }) {
  const conversations = useQuery(conversationsQuery(true));
  const conversationId =
    props.questions.find((q) => q.conversation_id)?.conversation_id ?? conversations.data?.find((c) => c.run_id === props.runId)?.id;
  if (!conversationId) return <ConversationCard source={{ kind: "none" }} {...props} />;
  // Keyed so the decrypted state restarts if the run's conversation changes.
  return <LiveConversation key={conversationId} device={device} conversationId={conversationId} {...props} />;
}

function LiveConversation({
  device,
  conversationId,
  ...props
}: ConversationSlotProps & { device: DeviceState & { kind: "linked" }; conversationId: string }) {
  const { state, send } = useConversation(device, conversationId);
  return (
    <ConversationCard
      source={{ kind: "live", state, access: { kind: "linked", deviceId: device.device.deviceId, send } }}
      {...props}
    />
  );
}
